import { FileText, Printer } from "lucide-react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";

export const AVV_VERSION = "23.09.2026";
export const AVV_VERSION_ID = "2026-09-23";

function Section({ title, children }) {
  return (
    <section className="space-y-2">
      <h3 className="font-semibold text-[#1D1D1F]">{title}</h3>
      <div className="space-y-2 text-sm leading-6 text-[#3A3A3C]">{children}</div>
    </section>
  );
}

export default function DataProcessingAgreementDialog({
  open,
  onOpenChange,
  customerName = "Auftraggeber",
  acceptedAt,
  onAccept,
  accepting = false,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[min(90dvh,860px)] max-w-3xl flex-col overflow-hidden p-0">
        <DialogHeader className="border-b border-[#E5E5EA] px-5 py-4 sm:px-6">
          <DialogTitle className="flex items-center gap-2 text-left">
            <FileText className="h-5 w-5" />
            Auftragsverarbeitungsvertrag
          </DialogTitle>
          <p className="text-left text-xs text-[#86868B]">Gemäß Art. 28 DSGVO · Version {AVV_VERSION}</p>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-5 py-5 sm:px-6" id="avv-document">
          <div className="rounded-xl bg-[#F5F5F7] p-4 text-sm leading-6 text-[#1D1D1F]">
            <p><strong>Auftraggeber:</strong> {customerName || "Organisation laut Vertrag"}</p>
            <p><strong>Auftragnehmer:</strong> Make It Media GmbH, Im Kohnental 2, 52353 Düren</p>
          </div>

          <Section title="1. Gegenstand und Dauer">
            <p>Der Auftragnehmer stellt Make It Booking zur Terminverwaltung, Kunden- und Patientenverwaltung, Kommunikation, Dokumentation sowie optionalen Abrechnung bereit. Die Verarbeitung dauert für die Laufzeit des Nutzungsvertrags und die anschließende vertragsgemäße Rückgabe oder Löschung.</p>
          </Section>

          <Section title="2. Art und Zweck der Verarbeitung">
            <p>Personenbezogene Daten werden ausschließlich zur Bereitstellung, Sicherung, Wartung und Unterstützung der vom Auftraggeber gewählten Funktionen verarbeitet. Eine Verarbeitung zu eigenen Werbe- oder Profilbildungszwecken findet nicht statt.</p>
          </Section>

          <Section title="3. Daten und betroffene Personen">
            <p>Je nach Nutzung werden Stamm- und Kontaktdaten, Termin- und Kommunikationsdaten, Benutzer- und Protokolldaten, Dokumente, Abrechnungsdaten sowie vom Auftraggeber eingestellte Gesundheits- und Behandlungsdaten verarbeitet.</p>
            <p>Betroffen sind insbesondere Patienten, Kunden, Zahlungspflichtige, Beschäftigte und Benutzer des Auftraggebers.</p>
          </Section>

          <Section title="4. Weisungen und Vertraulichkeit">
            <p>Der Auftragnehmer verarbeitet Daten nur auf dokumentierte Weisung des Auftraggebers oder aufgrund einer gesetzlichen Verpflichtung. Zugriffsberechtigte Personen werden auf Vertraulichkeit verpflichtet. Dies umfasst bei der Verarbeitung von Patientengeheimnissen auch die Anforderungen an mitwirkende Personen nach § 203 StGB.</p>
            <p>Hält der Auftragnehmer eine Weisung für datenschutzwidrig, informiert er den Auftraggeber unverzüglich und setzt die betroffene Verarbeitung bis zur Klärung aus, soweit keine gesetzliche Pflicht entgegensteht.</p>
          </Section>

          <Section title="5. Sicherheit und Unterstützung">
            <p>Der Auftragnehmer setzt risikogerechte technische und organisatorische Maßnahmen nach Art. 32 DSGVO ein. Dazu gehören insbesondere:</p>
            <ul className="list-disc space-y-1 pl-5">
              <li>verschlüsselte Datenübertragung und geschützte Administrationszugänge,</li>
              <li>rollenbasierte Berechtigungen und technische Mandantentrennung,</li>
              <li>Protokollierung sicherheitsrelevanter Vorgänge und geregelte Zugriffsvergabe,</li>
              <li>Datensicherung, Wiederherstellungsverfahren und Maßnahmen zur Verfügbarkeit,</li>
              <li>Verfahren zur Prüfung, Bewertung und Verbesserung der Schutzmaßnahmen.</li>
            </ul>
            <p>Der Auftragnehmer unterstützt den Auftraggeber im vereinbarten Umfang bei Betroffenenrechten, Datenschutzverletzungen, Datenschutz-Folgenabschätzungen und behördlichen Konsultationen. Bekannt gewordene Verletzungen des Schutzes personenbezogener Daten werden dem Auftraggeber unverzüglich mit den verfügbaren Informationen gemeldet.</p>
          </Section>

          <Section title="6. Unterauftragnehmer">
            <p>Der Auftraggeber erteilt eine allgemeine Genehmigung zum Einsatz der nachfolgend genannten Unterauftragnehmer. Änderungen werden vor ihrem Wirksamwerden mitgeteilt; der Auftraggeber kann aus wichtigem datenschutzrechtlichem Grund widersprechen.</p>
            <div className="overflow-x-auto rounded-xl border border-[#E5E5EA]">
              <table className="w-full min-w-[560px] text-left text-sm">
                <thead className="bg-[#F5F5F7] text-[#636366]">
                  <tr><th className="px-3 py-2 font-medium">Anbieter</th><th className="px-3 py-2 font-medium">Aufgabe</th><th className="px-3 py-2 font-medium">Datenstandort</th></tr>
                </thead>
                <tbody>
                  <tr className="border-t border-[#E5E5EA]">
                    <td className="px-3 py-3 align-top"><strong>Hetzner Online GmbH</strong></td>
                    <td className="px-3 py-3 align-top">Hosting der Anwendung und Serverbetrieb</td>
                    <td className="px-3 py-3 align-top">Deutschland / Europäische Union</td>
                  </tr>
                  <tr className="border-t border-[#E5E5EA]">
                    <td className="px-3 py-3 align-top"><strong>Supabase, Inc.</strong></td>
                    <td className="px-3 py-3 align-top">Authentifizierung, Datenbank und Dateispeicher</td>
                    <td className="px-3 py-3 align-top">Primäre Projektdaten in der konfigurierten EU-Region; mögliche Support- und Unterauftragnehmerzugriffe nach Supabase-DPA</td>
                  </tr>
                  <tr className="border-t border-[#E5E5EA]">
                    <td className="px-3 py-3 align-top"><strong>Twilio Inc.</strong></td>
                    <td className="px-3 py-3 align-top">SMS-Versand, sofern die SMS-Funktion aktiviert ist</td>
                    <td className="px-3 py-3 align-top">Internationale Verarbeitung nach Twilio-DPA und den dort beschriebenen Garantien</td>
                  </tr>
                  <tr className="border-t border-[#E5E5EA]">
                    <td className="px-3 py-3 align-top"><strong>Plus Five Five, Inc. (Resend)</strong></td>
                    <td className="px-3 py-3 align-top">Versand von Termin- und System-E-Mails</td>
                    <td className="px-3 py-3 align-top">Internationale Verarbeitung nach Resend-DPA einschließlich EU-Standardvertragsklauseln</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p>Vertragsinformationen: <a className="text-[#0071E3] underline" href="https://www.hetzner.com/AV/DPA_de.pdf" target="_blank" rel="noopener noreferrer">Hetzner-AVV</a>{" · "}<a className="text-[#0071E3] underline" href="https://supabase.com/downloads/docs/Supabase%2BDPA%2B250805.pdf" target="_blank" rel="noopener noreferrer">Supabase-DPA</a>{" · "}<a className="text-[#0071E3] underline" href="https://www.twilio.com/en-us/legal/data-protection-addendum" target="_blank" rel="noopener noreferrer">Twilio-DPA</a>{" · "}<a className="text-[#0071E3] underline" href="https://resend.com/legal/dpa" target="_blank" rel="noopener noreferrer">Resend-DPA</a></p>
            <p className="text-xs text-[#86868B]">Weitere optionale Dienste, etwa für Zahlung oder Kalender, werden nur bei Aktivierung der jeweiligen Funktion eingesetzt und sind anhand der tatsächlichen Konfiguration gesondert zu prüfen.</p>
          </Section>

          <Section title="7. Pflichten des Auftraggebers">
            <p>Der Auftraggeber bleibt für die Rechtmäßigkeit der Verarbeitung, die Zulässigkeit seiner Weisungen, die Information der betroffenen Personen und die Wahrung ihrer Rechte verantwortlich. Er teilt dem Auftragnehmer Fehler, Unregelmäßigkeiten und erforderliche Weisungen ohne unnötige Verzögerung mit.</p>
          </Section>

          <Section title="8. Löschung, Nachweise und Schlussbestimmungen">
            <p>Nach Vertragsende werden personenbezogene Daten nach Wahl und Weisung des Auftraggebers zurückgegeben oder gelöscht, soweit keine gesetzliche Aufbewahrungspflicht entgegensteht. Der Auftragnehmer stellt die zum Nachweis erforderlichen Informationen bereit und ermöglicht angemessene Kontrollen und Audits. Prüfungen sind unter Wahrung der Vertraulichkeit und mit angemessener Vorankündigung durchzuführen.</p>
            <p>Bei Widersprüchen gehen die datenschutzrechtlichen Regelungen dieses AVV für die Auftragsverarbeitung vor. Änderungen bedürfen der dokumentierten Textform.</p>
          </Section>
        </div>

        <DialogFooter className="border-t border-[#E5E5EA] px-5 py-4 sm:px-6">
          {acceptedAt ? (
            <p className="mr-auto text-sm text-emerald-700">Angenommen am {new Date(acceptedAt).toLocaleString("de-DE")}</p>
          ) : null}
          <Button type="button" variant="outline" onClick={() => window.print()}>
            <Printer className="mr-2 h-4 w-4" />
            Drucken
          </Button>
          {onAccept && !acceptedAt ? (
            <Button type="button" onClick={onAccept} disabled={accepting}>
              {accepting ? "Wird gespeichert..." : "AVV verbindlich annehmen"}
            </Button>
          ) : null}
          <Button type="button" onClick={() => onOpenChange(false)}>Schließen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
